"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success:
          "border-transparent bg-green-500/80 text-white hover:bg-green-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [parentState, setParentState] = React.useState<string | null>(null);

  React.useEffect(() => {
    const checkParentState = () => {
      if (!ref.current) return;
      
      const parent = ref.current.closest('[data-state]');
      if (parent) {
        const state = parent.getAttribute('data-state');
        setParentState(state);
      }
    };

    checkParentState();
    
    // Setup a mutation observer to detect parent state changes
    const observer = new MutationObserver(checkParentState);
    if (ref.current?.parentElement) {
      observer.observe(ref.current.parentElement, {
        attributes: true,
        attributeFilter: ['data-state'],
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={ref}
      data-parent-state={parentState}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
